
import {cc}                          from "@Common/react/callback-chain";
import {cs}                          from "@Common/react/chain-services";
import {scopedState}                 from "@Common/react/scoped-state";
import {UseState}                    from "@Common/react/use-state";
import {cnv}                         from "@Common/utils/arr-path";
import {cascadeCollect}              from "@Common/utils/cascade";
import {sort, toGroupsByKey, unique} from "@Common/utils/collections";
import {addDate, le, today}          from "@Common/utils/date-object";
import {chain}                       from "@Common/utils/fs";
import {filterKeys, keepOnly}        from "@Common/utils/objects";

import {groupEnrollmentEditors} from "../../../common/group-enrollment-editors";

import {isEnrollable}          from "./common/is-enrollable";
import {parseDate}             from "./common/parse-date";
import {ForSelectEditionsStep} from "./select-editions/for-select-editions-step";
import {SelectEditionsLayout}  from "./select-editions/select-editions-layout";
import {TemplateList}          from "./template-list/template-list";

export const Editions = ({initEnrollment, program, getStyleTemplate, next}) => cs(
    ["selectedEditions", ({}, next) => UseState({
        getInitValue: () => chain(
            initEnrollment?.editionsPreferences?.map((s) => keepOnly(s, ["editionId", "comment"])), // Omit because templateId is calculated base on program's series
            (_) => ({
                current: _,
                saved: _,
            }),
        ),
        next,
    })],

    ["forceEnrollEditionIds", (_, next) => next((() => {
        const seriesIds = program.programPreferences.series.filter((s) => s.forceAutoEnroll).map((s) => s.id);
        return program.programPreferences.editions.filter((pe) => seriesIds.some((sid) => sid == pe.seriesId)).map((e) => e.editionId);
    })())],
    ["isValidTemplate", (_, next) => next((templateId) => !!getStyleTemplate(templateId))],
    ["calValue", ({selectedEditions, forceEnrollEditionIds, isValidTemplate}, next) => next((() => {
        const editionIds = selectedEditions.value.current == null ? (
            program.programPreferences.editions.map((e) => e.editionId)
        ) : chain(
            [
                ...forceEnrollEditionIds ?? [],
                ...selectedEditions.value.current.map((ue) => ue.editionId) ?? [],
            ],
            unique,
        );
        const filteredEditionIds = editionIds.filter((editionId) => {
            const pe = program.programPreferences.editions.find((pe) => pe.editionId === editionId);
            return (
                pe &&
                isValidTemplate(pe.templateId) &&

                // If post deadline (not enrollable) then auto unenroll this edition when the enrollment is not completed
                (initEnrollment?.enrolled || isEnrollable(pe)) &&
                true
            );
        });

        return filteredEditionIds.map((editionId) => (
            selectedEditions.value.current?.find((ue) => ue.editionId == editionId) ?? ({editionId})
        ));
    })())],

    ["seriesList", ({isValidTemplate}, next) => next((() => {
        const groups = chain(
            program.programPreferences.editions,
            (_) => toGroupsByKey(_, (pe) => pe.seriesId),
            (_) => filterKeys<any>(_, (seriesId) => !_[seriesId].some((pe) => !isValidTemplate(pe.templateId))),
        );
        return chain(
            Object.keys(groups),
            (_) => sort(_, (seriesId) => groups[seriesId][0].enrollmentDeadline),
            (_) => _.map((seriesId) => ({
                series: program.programPreferences.series.find((s) => s.id === seriesId),
                editions: groups[seriesId],
            })),
        );
    })())],
    ["forSelectEditionsStep", ({calValue, seriesList, selectedEditions, forceEnrollEditionIds}, next) => ForSelectEditionsStep({
        calValue,
        selectedEditions,
        seriesList: chain(
            seriesList,
            (_) => _.map((r) => ({
                ...r,
                editions: r.editions.filter(isEnrollable),
            })),
            (_) => _.filter((r) => r.editions?.length),
        ),
        isUnselectable: (editionId) => !forceEnrollEditionIds.some((eid) => eid == editionId),
        initEnrollment,
        program,
        next,
    })],

    ({selectedEditions, calValue, forSelectEditionsStep, seriesList}) => {
        const peList = chain(
            program.programPreferences.editions,
            (_) => _.filter((e) => calValue?.some((e1) => e1.editionId === e.editionId)),
            (_) => sort<any>(_, (e) => e.enrollmentDeadline),
        );

        const selectedTemplateIds = chain(
            peList,
            (_) => _.some(isUpcoming) ? _.filter(isUpcoming) : _,
            (_) => _.map((pe) => pe.templateId),
            unique,
        );
        return next({
            upcomingEdition: peList.find(isUpcoming),
            selectedTemplateIds,
            allTemplateIds: chain(
                [
                    ...cascadeCollect(seriesList, "[*].editions[*].templateId"),
                    ...selectedTemplateIds,
                ],
                unique,
            ),

            selectEditions: {
                enrollmentEditor: groupEnrollmentEditors([
                    forSelectEditionsStep.enrollmentEditor,
                    {
                        $name: "SC > Editions > Series",
                        valid: true,
                        prepareSave: (enrollment) => {
                            const forcedSeriesIds = chain(
                                calValue,
                                (_) => _.map(({editionId}) => (
                                    program.programPreferences.editions.find((e) => e.editionId === editionId)?.seriesId
                                )),
                                (_) => _.filter(v=>v),
                                unique,
                            );
                            return ({
                                newValue: cnv(enrollment, ["seriesPreferences"], (_) => [
                                    ..._ ?? [],
                                    ...forcedSeriesIds.filter((id) => !_?.some((s) => s.id === id)).map((id) => ({
                                        id,
                                    }))
                                ]),
                            });
                        },
                    },
                ].filter(v=>v)),
                render: ({templateOptionsList}) => SelectEditionsLayout({
                    topControl: forSelectEditionsStep.topControl,
                    content: forSelectEditionsStep.render({
                        templateOptionsList,
                        getStyleTemplate,
                    }),
                }),
            },
            seriesSummary: cc(calValue, [
                (_, next) => next(_.map((ue) => {
                    const pe = program.programPreferences.editions.find((s1) => s1.editionId === ue.editionId);
                    return {ue, pe};
                })),
                (_) => {
                    const groups = toGroupsByKey(_, ({pe}) => pe.seriesId);
                    return (
                        sort(Object.keys(groups), (seriesId) => groups[seriesId][0].pe.enrollmentDeadline)
                            .map((seriesId) => ({
                                name: program.programPreferences.series.find((s) => s.id === seriesId).name,
                                editions: chain(
                                    groups[seriesId],
                                    (_) => sort<any>(_, ({pe}) => pe.enrollmentDeadline).map(({pe}) => ({
                                        name: pe.name,
                                        templateId: pe.templateId,
                                    })),
                                ),
                            }))
                    )
                },
            ]),

            editionList: {
                render: () => TemplateList({
                    program,
                    getStyleTemplate,
                    editions: {
                        ...scopedState(selectedEditions, ["current"]),
                        value: calValue,
                    },
                }),
            },
        });
    },
);

const isUpcoming = (edition) => le(today(), addDate(parseDate(edition.orderReviewDeadline), 2));


